import React, { useState, useEffect } from 'react';
import { getClasses, getClassReport, getMonthlyReport, addNotice } from '../../api';
import { toast } from 'react-toastify';

const ClassReports = () => {
  const [classes, setClasses] = useState([]);
  const [selectedClass, setSelectedClass] = useState('');
  const [report, setReport] = useState([]);
  const [reportType, setReportType] = useState('weekly');
  const [loading, setLoading] = useState(false);

  useEffect(() => { loadClasses(); }, []);

  const loadClasses = async () => {
    try {
      const res = await getClasses();
      setClasses(res.data);
    } catch (err) {
      toast.error('Error loading classes');
    }
  };

  const loadReport = async () => {
    if (!selectedClass) return toast.error('Select a class first!');
    setLoading(true);
    try {
      const res = reportType === 'weekly'
        ? await getClassReport(selectedClass)
        : await getMonthlyReport(selectedClass);
      setReport(res.data);
    } catch (err) {
      toast.error('Error loading report');
    }
    setLoading(false);
  };

  const postToNoticeBoard = async () => {
    if (report.length === 0) return toast.error('Load report first!');
    const className = classes.find(c => c.id === parseInt(selectedClass));
    const top3 = report.slice(0, 3);
    const reportText = top3.map((s, i) =>
      `${i + 1}. ${s.student_name} - ${s.percentage}%`
    ).join('\n');

    const title = `${reportType === 'weekly' ? 'Weekly' : 'Monthly'} Top Students - ${className?.name} ${className?.group_name}`;
    const content = `🏆 Top Performing Students:\n\n${reportText}\n\nReport Date: ${new Date().toLocaleDateString()}`;

    try {
      await addNotice({ title, content });
      toast.success('Report posted to Notice Board!');
    } catch (err) {
      toast.error('Error posting notice');
    }
  };

  const getGrade = (percentage) => {
    if (percentage >= 90) return 'A+';
    if (percentage >= 80) return 'A';
    if (percentage >= 70) return 'B';
    if (percentage >= 60) return 'C';
    if (percentage >= 50) return 'D';
    return 'F';
  };

  const getMedalColor = (index) => {
    if (index === 0) return '#FFD700';
    if (index === 1) return '#C0C0C0';
    if (index === 2) return '#CD7F32';
    return null;
  };

  return (
    <div>
      <h3 style={styles.title}>📈 Class Performance Reports</h3>

      <div style={styles.filterCard}>
        <div style={styles.filterGrid}>
          <div>
            <label style={styles.label}>Select Class</label>
            <select style={styles.input} value={selectedClass}
              onChange={e => setSelectedClass(e.target.value)}>
              <option value="">-- Select Class --</option>
              {classes.map(c => (
                <option key={c.id} value={c.id}>
                  {c.name} {c.section} - {c.group_name}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label style={styles.label}>Report Type</label>
            <select style={styles.input} value={reportType}
              onChange={e => setReportType(e.target.value)}>
              <option value="weekly">Weekly Report</option>
              <option value="monthly">Monthly Report</option>
            </select>
          </div>
          <div style={styles.btnContainer}>
            <button style={styles.loadBtn} onClick={loadReport} disabled={loading}>
              {loading ? 'Loading...' : '📊 Generate Report'}
            </button>
          </div>
        </div>
      </div>

      {report.length > 0 && (
        <>
          <div style={styles.reportHeader}>
            <h4 style={styles.reportTitle}>
              {reportType === 'weekly' ? '📅 Weekly' : '📆 Monthly'} Report —
              {classes.find(c => c.id === parseInt(selectedClass))?.name} 
              {classes.find(c => c.id === parseInt(selectedClass))?.group_name}
            </h4>
            <button style={styles.noticeBtn} onClick={postToNoticeBoard}>
              📌 Post to Notice Board
            </button>
          </div>

          <div style={styles.topThree}>
            {report.slice(0, 3).map((student, index) => (
              <div key={student.student_id} style={{
                ...styles.topCard,
                borderTop: `4px solid ${getMedalColor(index)}`
              }}>
                <div style={styles.medal}>
                  {index === 0 ? '🥇' : index === 1 ? '🥈' : '🥉'}
                </div>
                <h4 style={styles.topName}>{student.student_name}</h4>
                <p style={styles.topRoll}>Roll: {student.roll_number || '-'}</p>
                <h3 style={{
                  ...styles.topPercentage,
                  color: student.percentage >= 60 ? '#155724' : '#721c24'
                }}>
                  {student.percentage || 0}%
                </h3>
                <span style={styles.topGrade}>
                  Grade: {getGrade(student.percentage)}
                </span>
              </div>
            ))}
          </div>

          <div style={styles.tableCard}>
            <table style={styles.table}>
              <thead>
                <tr style={styles.tableHeader}>
                  <th style={styles.th}>Rank</th>
                  <th style={styles.th}>Roll No</th>
                  <th style={styles.th}>Student Name</th>
                  <th style={styles.th}>Exams</th>
                  <th style={styles.th}>Obtained</th>
                  <th style={styles.th}>Total</th>
                  <th style={styles.th}>Percentage</th>
                  <th style={styles.th}>Grade</th>
                </tr>
              </thead>
              <tbody>
                {report.map((student, index) => (
                  <tr key={student.student_id} style={{
                    ...styles.tableRow,
                    background: index < 3 ? '#fffbf0' : 'white'
                  }}>
                    <td style={styles.td}>
                      {index < 3
                        ? (index === 0 ? '🥇' : index === 1 ? '🥈' : '🥉')
                        : `#${index + 1}`
                      }
                    </td>
                    <td style={styles.td}>{student.roll_number || '-'}</td>
                    <td style={styles.td}>
                      <strong>{student.student_name}</strong>
                    </td>
                    <td style={styles.td}>{student.total_exams || 0}</td>
                    <td style={styles.td}>{student.total_obtained || 0}</td>
                    <td style={styles.td}>{student.total_possible || 0}</td>
                    <td style={styles.td}>
                      <div style={styles.progressBar}>
                        <div style={{
                          ...styles.progressFill,
                          width: `${student.percentage || 0}%`,
                          background: student.percentage >= 60 ? '#28a745' : '#dc3545'
                        }} />
                        <span style={styles.progressText}>
                          {student.percentage || 0}%
                        </span>
                      </div>
                    </td>
                    <td style={styles.td}>
                      <span style={styles.gradeBadge}>
                        {getGrade(student.percentage)}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
};

const styles = {
  title: { color: '#1a3a5c', marginBottom: '20px' },
  filterCard: { background: 'white', borderRadius: '12px', padding: '20px', marginBottom: '20px', boxShadow: '0 2px 10px rgba(0,0,0,0.08)' },
  filterGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '15px', alignItems: 'end' },
  label: { fontSize: '13px', fontWeight: '600', color: '#555', display: 'block', marginBottom: '5px' },
  input: { padding: '10px', border: '1px solid #ddd', borderRadius: '6px', fontSize: '14px', width: '100%' },
  btnContainer: { display: 'flex', alignItems: 'flex-end' },
  loadBtn: { padding: '10px 20px', background: '#1a3a5c', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '14px', width: '100%' },
  reportHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' },
  reportTitle: { color: '#1a3a5c', margin: 0 },
  noticeBtn: { padding: '10px 20px', background: '#fd7e14', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '14px' },
  topThree: { display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '15px', marginBottom: '20px' },
  topCard: { background: 'white', borderRadius: '12px', padding: '20px', textAlign: 'center', boxShadow: '0 2px 10px rgba(0,0,0,0.08)' },
  medal: { fontSize: '40px', marginBottom: '10px' },
  topName: { color: '#1a3a5c', margin: '0 0 5px', fontSize: '16px' },
  topRoll: { color: '#666', fontSize: '13px', margin: '0 0 10px' },
  topPercentage: { margin: '0 0 5px', fontSize: '28px' },
  topGrade: { color: '#666', fontSize: '13px' },
  tableCard: { background: 'white', borderRadius: '12px', overflow: 'hidden', boxShadow: '0 2px 10px rgba(0,0,0,0.08)' },
  table: { width: '100%', borderCollapse: 'collapse' },
  tableHeader: { background: '#1a3a5c' },
  th: { padding: '12px 15px', color: 'white', textAlign: 'left', fontSize: '13px' },
  tableRow: { borderBottom: '1px solid #eee' },
  td: { padding: '12px 15px', fontSize: '13px', color: '#444' },
  progressBar: { position: 'relative', background: '#e9ecef', borderRadius: '10px', height: '20px', width: '100px' },
  progressFill: { position: 'absolute', left: 0, top: 0, height: '100%', borderRadius: '10px' },
  progressText: { position: 'absolute', width: '100%', textAlign: 'center', fontSize: '11px', fontWeight: 'bold', color: 'white', lineHeight: '20px' },
  gradeBadge: { padding: '3px 10px', background: '#e8f0fe', color: '#1a3a5c', borderRadius: '20px', fontSize: '12px', fontWeight: 'bold' },
};

export default ClassReports;